import { POSSIBLE_METRICS_ENUM } from "constants/metrics"
import { type MetricConfiguration } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics"
import {
  formatTableCSVCellByColumnData,
  generateDownloadCSV,
} from "helpers/download/download"
import { type ProjectType } from "types/ApiTypes"
import { capitalize } from "utils/lodash/capitalize"
import { groupBy } from "utils/lodash/groupBy"
import { uniqBy } from "utils/lodash/uniqBy"

const metricsWithParenthesis: Set<POSSIBLE_METRICS_ENUM> = new Set([
  POSSIBLE_METRICS_ENUM.FEES_SUPPLY_SIDE,
  POSSIBLE_METRICS_ENUM.EXPENSES,
  POSSIBLE_METRICS_ENUM.OPERATING_EXPENSES,
  POSSIBLE_METRICS_ENUM.TOKEN_INCENTIVES,
  POSSIBLE_METRICS_ENUM.COST_OF_REVENUE,
  POSSIBLE_METRICS_ENUM.SUPERCHAIN_FEES,
])

export function shouldMetricBeParenthesized(metric: POSSIBLE_METRICS_ENUM) {
  return metricsWithParenthesis.has(metric)
}

export const metricsWithIndent = [
  POSSIBLE_METRICS_ENUM.COST_OF_REVENUE,
  POSSIBLE_METRICS_ENUM.SUPERCHAIN_FEES,
  POSSIBLE_METRICS_ENUM.OPERATING_EXPENSES,
  POSSIBLE_METRICS_ENUM.TOKEN_INCENTIVES,
]

export const ITEMS_PER_PAGE = 5

export const SECTIONS = [
  "GMV",
  "Income statement",
  "Treasury",
  "Market data",
  "Valuation multiples",
  "Alternative KPIs",
]

export const METRICS_BY_SECTION: { [key: string]: POSSIBLE_METRICS_ENUM[] } = {
  GMV: [
    POSSIBLE_METRICS_ENUM.NET_DEPOSITS,
    POSSIBLE_METRICS_ENUM.ACTIVE_LOANS,
    POSSIBLE_METRICS_ENUM.ASSETS_STAKED,
    POSSIBLE_METRICS_ENUM.CAPITAL_DEPLOYED,
    POSSIBLE_METRICS_ENUM.OUTSTANDING_SUPPLY,
    POSSIBLE_METRICS_ENUM.TRADING_VOLUME,
    POSSIBLE_METRICS_ENUM.TRANSACTION_VOLUME,
    POSSIBLE_METRICS_ENUM.TRANSFER_VOLUME,
  ],
  "Income statement": [
    POSSIBLE_METRICS_ENUM.FEES,
    POSSIBLE_METRICS_ENUM.FEES_SUPPLY_SIDE,
    POSSIBLE_METRICS_ENUM.REVENUE,
    POSSIBLE_METRICS_ENUM.EXPENSES,
    POSSIBLE_METRICS_ENUM.COST_OF_REVENUE,
    POSSIBLE_METRICS_ENUM.SUPERCHAIN_FEES,
    POSSIBLE_METRICS_ENUM.OPERATING_EXPENSES,
    POSSIBLE_METRICS_ENUM.TOKEN_INCENTIVES,
    POSSIBLE_METRICS_ENUM.GROSS_PROFIT,
    POSSIBLE_METRICS_ENUM.EARNINGS,
  ],
  Treasury: [
    POSSIBLE_METRICS_ENUM.TREASURY,
    POSSIBLE_METRICS_ENUM.TREASURY_NET,
  ],
  "Market data": [
    POSSIBLE_METRICS_ENUM.PRICE,
    POSSIBLE_METRICS_ENUM.TOKEN_SUPPLY_CIRCULATING,
    POSSIBLE_METRICS_ENUM.MARKET_CAP_FULLY_DILUTED,
    POSSIBLE_METRICS_ENUM.MARKET_CAP_CIRCULATING,
    POSSIBLE_METRICS_ENUM.TOKEN_TRADING_VOLUME,
    POSSIBLE_METRICS_ENUM.TOKEN_TURNOVER_FULLY_DILUTED,
    POSSIBLE_METRICS_ENUM.TOKEN_TURNOVER_CIRCULATING,
    POSSIBLE_METRICS_ENUM.TOKENHOLDERS,
  ],
  "Valuation multiples": [
    POSSIBLE_METRICS_ENUM.PF_FULLY_DILUTED,
    POSSIBLE_METRICS_ENUM.PF_CIRCULATING,
    POSSIBLE_METRICS_ENUM.PS_FULLY_DILUTED,
    POSSIBLE_METRICS_ENUM.PS_CIRCULATING,
  ],
  "Alternative KPIs": [
    POSSIBLE_METRICS_ENUM.USER_DAU,
    POSSIBLE_METRICS_ENUM.USER_WAU,
    POSSIBLE_METRICS_ENUM.USER_MAU,
    POSSIBLE_METRICS_ENUM.ACTIVE_DEVELOPERS,
    POSSIBLE_METRICS_ENUM.CODE_COMMITS,
  ],
}

export const hasFinancialStatement = (
  project: Partial<Pick<ProjectType, "metric_availability">> | undefined
) => project?.metric_availability?.fees

export const downloadFinacialsCSV = (
  data: FinancialStatementDataType[] | undefined,
  project: ProjectType,
  showChanges: boolean,
  showDefinitions: boolean,
  metricConfigurations: Array<MetricConfiguration>
) => {
  if (!data) return

  const groupedData = groupBy(data, "section")
  const headings: string[] = uniqBy(data, "timestamp").map((d) =>
    showChanges
      ? `${d.timestamp_label_heading},${d.timestamp_label_heading} change`
      : d.timestamp_label_heading
  )
  const getHeader = () => {
    const header = ["Financial statement"]
    if (showDefinitions) return header.concat("Definition")
    return header
  }
  const csvHeader = getHeader().concat(headings).join(",")

  const fallbackData = Array(headings.length).fill({
    value: null,
    pop_change: null,
  })

  const csv = SECTIONS.map((section) => {
    const data = groupedData[section]
    // group data inside a section by metric id
    const groupedMetrics = groupBy(data, "metric_id")

    // we want to only show metrics which exist in project.metric_availability
    const projectMetrics = METRICS_BY_SECTION[section].filter(
      (metric) => typeof project.metric_availability[metric] === "boolean"
    )

    // map each metric key to build an array of values
    const resultStrings = projectMetrics.map((metric) => {
      const data = groupedMetrics[metric] || fallbackData

      const metricConfig = metricConfigurations.find(
        ({ metric_id }) => metric_id === metric
      )!
      const definition = capitalize(
        (data[0]?.definition || metricConfig.definition) ?? ""
      )

      const header = showDefinitions
        ? [metricConfig.title, `"${definition}"`]
        : [metricConfig.title]

      return header
        .concat(
          data.map((d) => {
            const formatted = formatTableCSVCellByColumnData({
              value: d.value,
              metric,
              aggregate: "values",
              metricConfigurations,
            }) as string

            const percentage = formatTableCSVCellByColumnData({
              value: d.pop_change,
              metric,
              aggregate: "changes",
              metricConfigurations,
            })

            return showChanges ? `${formatted},${percentage}` : formatted
          })
        )
        .join(",")
    })

    return resultStrings.join("\n")
  }).filter((val) => val.length > 0)

  csv.unshift(csvHeader)
  generateDownloadCSV({
    csv: csv.join("\r\n"),
    filename: `${project.name} financial statement.csv`,
  })
}

export const printPdf = (url: string) => {
  const iframe = document.createElement("iframe")
  document.body.appendChild(iframe)

  // need to set height and width high enough because of safari
  iframe.style.height = "1500px"
  iframe.style.width = "1500px"

  // display = 'none' does not work on safari..
  iframe.style.visibility = "hidden"

  iframe.onload = () => {
    setTimeout(() => {
      document.body.removeChild(iframe)
    }, 5000)
  }

  iframe.src = url
}

export type FinancialStatementDataType = {
  timestamp: string
  timestamp_label_heading: string
  timestamp_label_subheading: string
  timestamp_granularity: string
  project_id: string
  project_name: string
  section: string
  metric_id: string
  metric_name: string
  definition: string | null
  value: number | null
  pop_change: number | null
}
